import { alignVariantMetadataToLength } from "./alignVariantMetadataToLength";
import type { ComponentStoreData, VariantMetadata } from "./types";

const padEmptyStrings = (values: string[], length: number): string[] => {
  const emptyValuesToInclude = Math.max(0, length - values.length);
  if (emptyValuesToInclude > 0) {
    return [...values, ...Array(emptyValuesToInclude).fill("")];
  }
  return values;
};

const shouldTrackVariantMetadata = (
  componentName: string,
  variantMetadata: Record<string, VariantMetadata[]>
): boolean => {
  return componentName === "Image" || !!variantMetadata[componentName];
};

/**
 * Applies a component value change while keeping Image variantMetadata index-aligned.
 * When an Image URL changes (clear or replace), clears metadata at that index so the
 * previous image's attributes are not retained — and so URL+metadata stay atomic even when
 * ImageUploaderOrLiquidUrl fires clear-url then clear-alt as separate callbacks.
 *
 * @param store - Current component store
 * @param componentName - Component being edited
 * @param index - Variant index
 * @param nextValue - New URL / text at that index
 * @returns Next store snapshot
 */
export const applyComponentValueChange = (
  store: ComponentStoreData,
  componentName: string,
  index: number,
  nextValue: string
): ComponentStoreData => {
  const prevComponents = store.components ?? {};
  const prevMetadata = store.variantMetadata ?? {};
  const nextValues = [...(prevComponents[componentName] || [])];
  const previousValue = nextValues[index] ?? "";
  nextValues[index] = nextValue;

  const nextStore: ComponentStoreData = {
    ...store,
    components: { ...prevComponents, [componentName]: nextValues },
  };

  if (shouldTrackVariantMetadata(componentName, prevMetadata)) {
    const nextMetadata = alignVariantMetadataToLength(prevMetadata[componentName], nextValues.length);
    if (nextValue !== previousValue) {
      nextMetadata[index] = {};
    }
    nextStore.variantMetadata = { ...prevMetadata, [componentName]: nextMetadata };
  }

  return nextStore;
};

/**
 * Commits an Image URL and its complete metadata snapshot in one store update.
 * Unlike a metadata patch, this removes attributes that do not apply to the new asset.
 */
export const applyImageVariantChange = (
  store: ComponentStoreData,
  componentName: string,
  index: number,
  nextValue: string,
  metadata: VariantMetadata
): ComponentStoreData => {
  const prevComponents = store.components ?? {};
  const prevMetadata = store.variantMetadata ?? {};
  const nextValues = [...(prevComponents[componentName] || [])];
  nextValues[index] = nextValue;
  const nextMetadata = alignVariantMetadataToLength(prevMetadata[componentName], nextValues.length);
  nextMetadata[index] = { ...metadata };

  return {
    ...store,
    components: { ...prevComponents, [componentName]: nextValues },
    variantMetadata: { ...prevMetadata, [componentName]: nextMetadata },
  };
};

/**
 * Applies delete/clear of a variant while keeping Image variantMetadata index-aligned.
 *
 * - `clear`: empty the value and metadata at `index` (used when at minimum displayed rows).
 *   Always clears metadata, even if the URL was already empty — unlike
 *   `applyComponentValueChange`, which keeps metadata when the URL is unchanged.
 * - `remove`: splice value/metadata at `index`, then pad values to `targetLength`.
 *
 * @param store - Current component store
 * @param componentName - Component being edited
 * @param index - Variant index
 * @param options - Clear in place vs remove row and pad
 * @returns Next store snapshot
 */
export const applyComponentValueDelete = (
  store: ComponentStoreData,
  componentName: string,
  index: number,
  options: { mode: "clear" } | { mode: "remove"; targetLength: number }
): ComponentStoreData => {
  const prevComponents = store.components ?? {};
  const prevMetadata = store.variantMetadata ?? {};
  const currentValues = prevComponents[componentName] || [];
  const trackMetadata = shouldTrackVariantMetadata(componentName, prevMetadata);

  let nextValues: string[];
  let nextMetadata: VariantMetadata[] | undefined;

  if (options.mode === "clear") {
    nextValues = [...currentValues];
    nextValues[index] = "";
    if (trackMetadata) {
      nextMetadata = alignVariantMetadataToLength(prevMetadata[componentName], currentValues.length);
      nextMetadata[index] = {};
    }
  } else {
    nextValues = [...currentValues];
    nextValues.splice(index, 1);
    nextValues = padEmptyStrings(nextValues, options.targetLength);
    if (trackMetadata) {
      nextMetadata = alignVariantMetadataToLength(prevMetadata[componentName], currentValues.length);
      nextMetadata.splice(index, 1);
      nextMetadata = alignVariantMetadataToLength(nextMetadata, options.targetLength);
    }
  }

  const nextStore: ComponentStoreData = {
    ...store,
    components: { ...prevComponents, [componentName]: nextValues },
  };
  if (nextMetadata) {
    nextStore.variantMetadata = { ...prevMetadata, [componentName]: nextMetadata };
  }
  return nextStore;
};

/**
 * Replaces a component's values array and pads/truncates variantMetadata to the new length.
 * Does not clear existing metadata at surviving indices (unlike applyComponentValueChange).
 *
 * @param store - Current component store
 * @param componentName - Component being edited
 * @param nextValues - Next variant values for that component
 * @returns Next store snapshot
 */
export const applyComponentValues = (
  store: ComponentStoreData,
  componentName: string,
  nextValues: string[]
): ComponentStoreData => {
  const prevComponents = store.components ?? {};
  const prevMetadata = store.variantMetadata ?? {};
  const nextStore: ComponentStoreData = {
    ...store,
    components: { ...prevComponents, [componentName]: nextValues },
  };

  if (shouldTrackVariantMetadata(componentName, prevMetadata)) {
    nextStore.variantMetadata = {
      ...prevMetadata,
      [componentName]: alignVariantMetadataToLength(prevMetadata[componentName], nextValues.length),
    };
  }

  return nextStore;
};
